import { z } from 'zod'
import { isComplianceLinkedType } from '../types/tenancy'

// ============================================================
// COMMON VALIDATORS
// ============================================================

export const uuidSchema = z.string().uuid('Invalid ID format')

export const emailSchema = z.string().email('Invalid email format')

// Password validation: 8+ chars, at least 1 uppercase, 1 lowercase, 1 number
export const passwordSchema = z.string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')

export function validatePassword(password: string): { valid: boolean; errors: string[] } {
  const errors: string[] = []
  if (password.length < 8) errors.push('At least 8 characters')
  if (!/[A-Z]/.test(password)) errors.push('One uppercase letter')
  if (!/[a-z]/.test(password)) errors.push('One lowercase letter')
  if (!/[0-9]/.test(password)) errors.push('One number')
  return { valid: errors.length === 0, errors }
}

// UK phone number validation (+44 or 0 prefix)
export const ukPhoneSchema = z.string().regex(
  /^(\+44|0)[\d\s]{10,11}$/,
  'Invalid UK phone number format'
)

// Non-empty trimmed string
export const requiredStringSchema = z.string().min(1, 'This field is required').trim()

// Optional trimmed string
export const optionalStringSchema = z.string().trim().optional()

// Monetary amount in pence (positive integer)
export const moneySchema = z.number().int().nonnegative('Amount must be non-negative')

// Date string in ISO format
export const dateSchema = z.string().datetime({ offset: true }).or(
  z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)')
)

// ============================================================
// FILE UPLOAD VALIDATORS
// ============================================================

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

// image/heic and image/heif are what an iPhone camera actually produces. Without
// them a phone photo is rejected before it reaches storage, which is why the
// storage buckets allow them too — client gate and bucket ceiling must agree.
const ALLOWED_IMAGE_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'
]
const ALLOWED_DOCUMENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  // Compliance certificates, receipts and ID documents are photographed as
  // often as they are scanned, and these paths use isDocument.
  'image/heic',
  'image/heif'
]

const DANGEROUS_EXTENSIONS = [
  '.exe', '.bat', '.cmd', '.sh', '.php', '.js', '.jar', '.py', '.rb',
  '.ps1', '.vbs', '.wsf', '.msi', '.dll', '.scr', '.pif', '.com'
]

export function validateFile(
  file: File,
  options: {
    maxSize?: number
    allowedTypes?: string[]
    isImage?: boolean
    isDocument?: boolean
  } = {}
): { valid: boolean; error?: string } {
  const maxSize = options.maxSize ?? MAX_FILE_SIZE
  const allowedTypes = options.allowedTypes ??
    (options.isImage ? ALLOWED_IMAGE_TYPES :
     options.isDocument ? ALLOWED_DOCUMENT_TYPES :
     [...ALLOWED_IMAGE_TYPES, ...ALLOWED_DOCUMENT_TYPES])

  // Check file size
  if (file.size > maxSize) {
    return { valid: false, error: `File size exceeds ${maxSize / (1024 * 1024)}MB limit` }
  }

  // Check MIME type
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: `File type '${file.type}' is not allowed` }
  }

  // Check for dangerous extensions
  const fileName = file.name.toLowerCase()
  for (const ext of DANGEROUS_EXTENSIONS) {
    if (fileName.endsWith(ext)) {
      return { valid: false, error: `File extension '${ext}' is not allowed` }
    }
  }

  // Check for double extensions
  const parts = fileName.split('.')
  if (parts.length > 2) {
    for (let i = 0; i < parts.length - 1; i++) {
      if (DANGEROUS_EXTENSIONS.includes(`.${parts[i]}`)) {
        return { valid: false, error: 'Suspicious file extension detected' }
      }
    }
  }

  return { valid: true }
}

export function sanitizeFilename(filename: string): string {
  // Remove path traversal characters and sanitize
  return filename
    .replace(/\.\./g, '')
    .replace(/[/\\]/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .substring(0, 255)
}

// ============================================================
// INPUT SANITIZATION
// ============================================================

export function sanitizeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

// ============================================================
// ENTITY SCHEMAS
// ============================================================

export const prospectSchema = z.object({
  full_name: z.string().trim().min(1).max(255),
  email: emailSchema.optional().nullable(),
  phone: ukPhoneSchema.optional().nullable(),
  source: z.enum(['website', 'referral', 'rightmove', 'zoopla', 'openrent', 'spareroom', 'other']).optional(),
  referral_source: z.string().trim().max(255).optional(),
  budget_min_pence: z.number().int().nonnegative().optional().nullable(),
  budget_max_pence: z.number().int().nonnegative().optional().nullable(),
  move_in_date: dateSchema.optional().nullable(),
  notes: z.string().trim().max(5000).optional(),
})

export const issueSchema = z.object({
  property_id: uuidSchema,
  unit_id: uuidSchema.optional().nullable(),
  // room_id removed: a room is a unit (unit_type='room'), so the location
  // is a single unit_id. Migrations 464-465.

  asset_id: uuidSchema.optional().nullable(),
  title: z.string().trim().min(1).max(255),
  description: z.string().trim().max(5000).optional(),
  category: z.enum(['plumbing', 'electrical', 'heating', 'structural', 'appliance', 'pest', 'security', 'cleaning', 'garden', 'other']),
  urgency: z.enum(['low', 'medium', 'high', 'emergency']),
  preferred_days: z.array(z.string()).optional(),
  preferred_times: z.array(z.string()).optional(),
})

export const commentSchema = z.object({
  issue_id: uuidSchema,
  body: requiredStringSchema.max(5000),
})

export const contractorSchema = z.object({
  contractor_name: z.string().trim().min(1).max(255),
  contractor_email: emailSchema,
  contractor_phone: ukPhoneSchema.optional(),
  contractor_company: z.string().trim().max(255).optional(),
  trade: z.enum(['plumber', 'electrician', 'gas_engineer', 'builder', 'roofer', 'locksmith', 'cleaner', 'gardener', 'pest_control', 'general', 'other']),
})

export const expenseSchema = z.object({
  property_id: uuidSchema,
  category: z.enum(['maintenance', 'repairs', 'utilities', 'insurance', 'mortgage', 'management', 'legal', 'marketing', 'cleaning', 'garden', 'furnishings', 'other']),
  description: z.string().trim().min(1).max(500),
  amount: moneySchema,
  expense_date: dateSchema,
  is_tax_deductible: z.boolean().default(true),
  tax_year: z.string().regex(/^\d{4}\/\d{2}$/).optional(),
  notes: z.string().trim().max(1000).optional(),
})

export const rentPaymentSchema = z.object({
  tenancy_id: uuidSchema,
  amount: moneySchema,
  due_date: dateSchema,
  status: z.enum(['pending', 'paid', 'partial', 'overdue', 'waived']).default('pending'),
  paid_amount: moneySchema.optional().nullable(),
  paid_date: dateSchema.optional().nullable(),
  payment_method: z.enum(['bank_transfer', 'standing_order', 'cash', 'cheque', 'card', 'other']).optional().nullable(),
  payment_reference: z.string().trim().max(100).optional(),
  notes: z.string().trim().max(1000).optional(),
})

export const profileSchema = z.object({
  full_name: z.string().trim().min(1).max(255),
  email: emailSchema,
  phone: ukPhoneSchema.optional().nullable(),
  role: z.enum(['landlord', 'tenant', 'contractor', 'agent', 'admin']),
  company_name: z.string().trim().max(255).optional(),
})

export const tenancySchema = z.object({
  property_id: uuidSchema,
  unit_id: uuidSchema.optional().nullable(),
  // room_id removed: a room is a unit (unit_type='room'), so the location
  // is a single unit_id. Migrations 464-465.

  start_date: dateSchema,
  end_date: dateSchema.optional().nullable(),
  rent_amount_pence: moneySchema,
  deposit_amount_pence: moneySchema.optional().nullable(),
  rent_frequency: z.enum(['weekly', 'fortnightly', 'monthly', 'quarterly', 'annually']).default('monthly'),
  status: z.enum(['draft', 'pending', 'active', 'ended', 'terminated']).default('draft'),
})

export const tenantDocumentTypeSchema = z.enum([
  'gas_safety', 'eicr', 'epc', 'deposit_cert',
  'inventory', 'ast', 'other', 'written_statement_of_terms', 'government_information_sheet',
  'prescribed_information',
])

export const documentServeSchema = z.object({
  tenancyId: uuidSchema,
  documentType: tenantDocumentTypeSchema,
  documentName: z.string().trim().min(1, 'Document name is required').max(255),
  fileUrl: z.string().max(1000).nullable(),
  createdBy: uuidSchema,
  complianceDocumentId: uuidSchema.optional(),
}).refine(
  (data) => !isComplianceLinkedType(data.documentType) || !!data.complianceDocumentId,
  { message: 'complianceDocumentId required for compliance document types (Gas Safety, EICR, EPC)', path: ['complianceDocumentId'] }
)

export const acknowledgeDocumentWithMetadataSchema = z.object({
  documentId: uuidSchema,
  ipAddress: z.string().max(45).optional(),
  userAgent: z.string().max(500).optional(),
})

export const sendDocumentReminderSchema = z.object({
  documentId: uuidSchema,
  tenantId: uuidSchema,
  sentBy: uuidSchema,
})

export const deleteTenantDocumentSchema = z.object({
  documentId: uuidSchema,
  deletedBy: uuidSchema,
})

// ============================================================
// AGREEMENT SCHEMAS
// ============================================================

export const signAgreementSchema = z.object({
  agreementId: uuidSchema,
  signatureDataUrl: z.string().min(1, 'Signature is required'),
  ipAddress: z.string().max(45).optional(),
})

export const countersignAgreementSchema = z.object({
  agreementId: uuidSchema,
  signatureDataUrl: z.string().min(1, 'Signature is required'),
  onBehalfOf: z.string().max(255).optional(),
  ipAddress: z.string().max(45).optional(),
})

export const serveWrittenStatementSchema = z.object({
  agreementId: uuidSchema,
  tenancyId: uuidSchema,
  templateType: z.enum(['written_statement', 'tenancy_agreement', 'information_sheet']).optional(),
  documentName: z.string().trim().max(255).optional(),
})

export const createGeneratedAgreementSchema = z.object({
  tenancyId: uuidSchema,
  templateId: uuidSchema,
  templateVersion: z.number().int().positive(),
  additionalClauses: z.string().max(10000).optional(),
})

export const supersedeAgreementSchema = z.object({
  oldId: uuidSchema,
  newId: uuidSchema,
})

// ============================================================
// ADMIN SCHEMAS
// ============================================================

export const adminUserUpdateSchema = z.object({
  full_name: z.string().trim().max(255).optional(),
  phone: ukPhoneSchema.optional().nullable(),
  role: z.enum(['landlord', 'tenant', 'contractor', 'agent', 'admin']).optional(),
  is_suspended: z.boolean().optional(),
})

export const platformSettingSchema = z.object({
  key: z.string().trim().min(1).max(100),
  value: z.unknown(),
  description: z.string().trim().max(500).optional(),
})

// ============================================================
// AGENT PORTFOLIO SCHEMAS
// ============================================================

export const portfolioSchema = z.object({
  name: z.string().trim().min(1, 'Portfolio name is required').max(255),
  client_name: z.string().trim().max(255).optional().nullable(),
  client_email: z.string().trim().email('Invalid email address').max(255).optional().nullable(),
  client_phone: ukPhoneSchema.optional().nullable(),
  notes: z.string().trim().max(2000).optional().nullable(),
  status: z.enum(['active', 'archived']).optional(),
  spend_approval_threshold_pence: z.number().int().min(0).optional().nullable(),
  management_fee_type: z.enum(['percentage', 'fixed']).optional().nullable(),
  management_fee_value: z.number().int().positive('Fee value must be positive').optional().nullable(),
  bank_account_name: z.string().trim().max(255).optional().nullable(),
  bank_sort_code: z.string().regex(/^\d{6}$/, 'Sort code must be exactly 6 digits').optional().nullable(),
  bank_account_number: z.string().regex(/^\d{8}$/, 'Account number must be exactly 8 digits').optional().nullable(),
})

export const portfolioUpdateSchema = portfolioSchema.partial()

// ============================================================
// TEAM MEMBER SCHEMAS
// ============================================================

export const teamMemberInviteSchema = z.object({
  email: z.string().trim().email('Invalid email address').max(255),
  role: z.enum(['admin', 'manager', 'viewer']),
  permissions: z.object({
    financial_access: z.boolean(),
  }).optional(),
})

export const teamMemberUpdateSchema = z.object({
  role: z.enum(['admin', 'manager', 'viewer']).optional(),
  permissions: z.object({
    financial_access: z.boolean(),
  }).optional(),
})

// ============================================================
// PROPERTY FORM SCHEMAS
// ============================================================

// UK postcode validation (case-insensitive, optional space)
export const ukPostcodeSchema = z.string()
  .trim()
  .min(1, 'Postcode is required')
  .regex(/^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i, 'Invalid UK postcode format')

export const propertyDetailsSchema = z.object({
  addressLine1: z.string().trim().min(1, 'Address line 1 is required').max(255),
  addressLine2: z.string().trim().max(255).optional(),
  city: z.string().trim().max(255).optional(),
  postcode: ukPostcodeSchema,
  propertyType: z.enum(['residential', 'residential_building', 'commercial', 'hmo']),
})

export const propertyTenancySchema = z.object({
  rentAmount: z.string().min(1, 'Rent amount is required').refine(
    v => !isNaN(parseFloat(v)) && parseFloat(v) > 0,
    'Rent amount must be greater than zero'
  ),
  rentFrequency: z.enum(['weekly', 'monthly']),
  rentDueDay: z.string().refine(
    v => { const n = parseInt(v); return n >= 1 && n <= 31 },
    'Due day must be between 1 and 31'
  ),
  startDate: z.string().min(1, 'Start date is required').regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format'),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format').or(z.literal('')),
  depositAmount: z.string().refine(
    v => v === '' || (!isNaN(parseFloat(v)) && parseFloat(v) >= 0),
    'Deposit must be a valid amount'
  ),
  depositScheme: z.enum(['dps', 'tds', 'mydeposits', 'other', '']),
  depositSchemeOther: z.string().trim().max(255),
  depositReference: z.string().trim().max(255),
  depositProtectedDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format').or(z.literal('')),
  depositType: z.enum(['custodial', 'insured', '']),
})

export const newTenantFormSchema = z.object({
  newTenantName: z.string().trim().min(1, 'Tenant name is required').max(255),
  newTenantEmail: emailSchema,
  newTenantPhone: z.union([ukPhoneSchema, z.literal('')]),
})

/** Extract a flat Record<string, string> of first error per field from a ZodError. */
export function extractZodErrors(error: z.ZodError): Record<string, string> {
  const errors: Record<string, string> = {}
  for (const issue of error.issues) {
    const field = issue.path.join('.')
    if (!errors[field]) errors[field] = issue.message
  }
  return errors
}

// ============================================================
// SCHEDULED MAINTENANCE SCHEMAS
// ============================================================

export const maintenanceFrequencySchema = z.enum([
  'weekly', 'fortnightly', 'monthly', 'quarterly',
  'semi_annually', 'annually', 'biennial', 'custom',
])

export const maintenanceCategorySchema = z.enum([
  'plumbing', 'electrical', 'heating', 'structural', 'appliance',
  'pest_control', 'damp_mould', 'locks_security', 'garden_exterior',
  'cleaning', 'fire_safety', 'gas_safety', 'general_inspection', 'other',
])

const scheduledMaintenanceBaseSchema = z.object({
  property_id: uuidSchema,
  unit_id: uuidSchema.nullable().optional(),
  asset_id: uuidSchema.nullable().optional(),
  title: z.string().trim().min(1, 'Title is required').max(200),
  description: z.string().trim().max(2000).nullable().optional(),
  category: maintenanceCategorySchema,
  urgency: z.enum(['low', 'medium', 'high']).default('medium'),
  compliance_document_type: z.string().nullable().optional(),
  preferred_contractor_id: uuidSchema.nullable().optional(),
  preferred_approved_contractor_id: uuidSchema.nullable().optional(),
  frequency: maintenanceFrequencySchema,
  custom_interval_days: z.number().int().positive().nullable().optional(),
  next_due_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)'),
  lead_time_days: z.number().int().min(0).max(90).default(14),
  estimated_cost_pence: z.number().int().nonnegative().nullable().optional(),
  auto_create_issue: z.boolean().default(false),
  is_active: z.boolean().default(true),
  notes: z.string().trim().max(2000).nullable().optional(),
})

export const scheduledMaintenanceInsertSchema = scheduledMaintenanceBaseSchema.refine(
  (data) => {
    if (data.frequency === 'custom') return data.custom_interval_days != null && data.custom_interval_days > 0
    return data.custom_interval_days == null || data.custom_interval_days === undefined
  },
  { message: 'custom_interval_days required for custom frequency, must be null otherwise' }
)

export const scheduledMaintenanceUpdateSchema = scheduledMaintenanceBaseSchema.partial()

// ============================================================
// COMPLIANCE DOCUMENT SCHEMAS
// ============================================================

export const complianceDocumentTypeSchema = z.enum([
  'gas_safety', 'eicr', 'epc', 'legionella', 'hmo_licence',
  'buildings_insurance', 'landlord_insurance', 'fire_safety', 'pat_testing', 'other',
])

export const complianceDocumentInsertSchema = z.object({
  property_id: uuidSchema,
  document_type: complianceDocumentTypeSchema,
  document_name: z.string().trim().max(255).optional().nullable(),
  issue_date: dateSchema.optional().nullable(),
  expiry_date: dateSchema.optional().nullable(),
  notes: z.string().trim().max(2000).optional().nullable(),
})

const confidenceLevelSchema = z.enum(['high', 'medium', 'low', 'no_text_extracted'])
const warningLevelSchema = z.enum(['green', 'amber', 'red', 'info'])

export const verificationConfirmationSchema = z.object({
  detection: z.object({
    declaredType: complianceDocumentTypeSchema,
    detectedKeywords: z.array(z.string()),
    matchedPatterns: z.array(z.string()),
    confidenceLevel: confidenceLevelSchema,
    warnings: z.array(z.string()),
  }),
  warningLevel: warningLevelSchema,
  checklistItems: z.array(z.object({
    label: z.string(),
    confirmed: z.literal(true),
  })),
  confirmedAt: z.string().datetime({ offset: true }).or(z.string()),
  aiExtractedData: z.object({
    is_correct_type: z.boolean(),
    detected_type: z.string().nullable(),
    expiry_date: z.string().nullable(),
    issue_date: z.string().nullable(),
    certificate_number: z.string().nullable(),
    engineer_name: z.string().nullable(),
    property_address: z.string().nullable(),
    confidence: z.enum(['high', 'medium', 'low']),
    warnings: z.array(z.string()),
  }).optional().nullable(),
})

// ============================================================
// TYPE EXPORTS
// ============================================================

export type ProspectInput = z.infer<typeof prospectSchema>
export type IssueInput = z.infer<typeof issueSchema>
export type CommentInput = z.infer<typeof commentSchema>
export type ContractorInput = z.infer<typeof contractorSchema>
export type ExpenseInput = z.infer<typeof expenseSchema>
export type RentPaymentInput = z.infer<typeof rentPaymentSchema>
export type ProfileInput = z.infer<typeof profileSchema>
export type TenancyInput = z.infer<typeof tenancySchema>
export type DocumentServeInput = z.infer<typeof documentServeSchema>
export type AcknowledgeDocumentWithMetadataInput = z.infer<typeof acknowledgeDocumentWithMetadataSchema>
export type SendDocumentReminderInput = z.infer<typeof sendDocumentReminderSchema>
export type DeleteTenantDocumentInput = z.infer<typeof deleteTenantDocumentSchema>
export type SignAgreementInput = z.infer<typeof signAgreementSchema>
export type CountersignAgreementInput = z.infer<typeof countersignAgreementSchema>
export type ServeWrittenStatementInput = z.infer<typeof serveWrittenStatementSchema>
export type CreateGeneratedAgreementInput = z.infer<typeof createGeneratedAgreementSchema>
export type SupersedeAgreementInput = z.infer<typeof supersedeAgreementSchema>
export type AdminUserUpdateInput = z.infer<typeof adminUserUpdateSchema>
export type PortfolioInput = z.infer<typeof portfolioSchema>
export type PortfolioUpdateInput = z.infer<typeof portfolioUpdateSchema>
export type TeamMemberInviteInput = z.infer<typeof teamMemberInviteSchema>
export type TeamMemberUpdateInput = z.infer<typeof teamMemberUpdateSchema>
export type ScheduledMaintenanceInsertInput = z.infer<typeof scheduledMaintenanceInsertSchema>
export type ScheduledMaintenanceUpdateInput = z.infer<typeof scheduledMaintenanceUpdateSchema>
export type ComplianceDocumentInsertInput = z.infer<typeof complianceDocumentInsertSchema>
export type VerificationConfirmationInput = z.infer<typeof verificationConfirmationSchema>
